import { useMutation, useQuery } from '@tanstack/react-query';

import competencyQuestionService, {
    type SaveCompetencyQuestionsPayload,
} from './competencyQuestionService';

import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { invalidateCompetencyTranslationQueries } from '@/api/competency-services/translation';
import { queryClient } from '@/AppWithProviders';
import { CompetencyQueryKey, CompetencyQuestionQueryKey } from '@/constants/QueryKeys';

const useGetCompetencyQuestions = (competencyId: string | null, enabled: boolean) => {
    return useQuery<ReviewQuestion[]>({
        queryKey: [CompetencyQuestionQueryKey.COMPETENCY_QUESTIONS, competencyId],
        queryFn: () => competencyQuestionService.getCompetencyQuestions(competencyId!),
        enabled: enabled && Boolean(competencyId),
    });
};

const useSaveCompetencyQuestions = () => {
    return useMutation({
        mutationFn: ({
            competencyId,
            payload,
        }: {
            competencyId: string;
            payload: SaveCompetencyQuestionsPayload;
        }) => competencyQuestionService.saveCompetencyQuestions(competencyId, payload),
        onSuccess: (data, variables) => {
            queryClient.setQueryData(
                [CompetencyQuestionQueryKey.COMPETENCY_QUESTIONS, variables.competencyId],
                data,
            );
            queryClient.invalidateQueries({
                queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS],
            });
            invalidateCompetencyTranslationQueries();
        },
    });
};

const useMoveCompetencyQuestion = () => {
    return useMutation({
        mutationFn: ({
            questionId,
            targetCompetencyId,
        }: {
            questionId: string;
            targetCompetencyId: string;
            sourceCompetencyId: string;
        }) => competencyQuestionService.moveCompetencyQuestion(questionId, targetCompetencyId),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [
                    CompetencyQuestionQueryKey.COMPETENCY_QUESTIONS,
                    variables.sourceCompetencyId,
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [
                    CompetencyQuestionQueryKey.COMPETENCY_QUESTIONS,
                    variables.targetCompetencyId,
                ],
            });
            queryClient.invalidateQueries({
                queryKey: [CompetencyQueryKey.COMPETENCY_GROUPS],
            });
            invalidateCompetencyTranslationQueries();
        },
    });
};

export { useGetCompetencyQuestions, useMoveCompetencyQuestion, useSaveCompetencyQuestions };
